import type {SocketData} from './signalr-socket'
import {SignalRSocket} from './signalr-socket'
import {StepState, bufferLogs, bufferedStepIds, stepsMap, updateStreamingLogs} from './steps'
import {attr, controller} from '@github/catalyst'
import type {CheckStepElement} from './check-step'
import {fetchJson} from './fetch'
import {sendStats} from '@github-ui/stats'

const MAX_RETRIES = 5
const RETRY_DELAY = 2000

interface StepData {
  id: string
  name: string
  status: string
  conclusion?: string
  started_at?: string
  completed_at?: string
  change_id: number
  completed_log_url?: string
  number: number
  log_url?: string
}

interface StepBackscrollLogs {
  lines: LogLine[]
}

interface LogLine {
  id: string
  line: string
}

interface AgentData {
  stepRecordId: string // uuid like external id
  lines: string[]
  startLine: number | null
}

// Incoming data over alive socket, see CheckRun.notify_socket_subscribers
interface AliveSocketData {
  timestamp: string
  reason: string
  wait: number
  status: 'requested' | 'queued' | 'in_progress' | 'completed' | 'waiting' | 'pending'
  has_steps: boolean
  raw_logs: boolean
  streaming_logs: boolean
}

interface ResultsLogLine {
  line: string
  lineID: string
}

interface StepUpdate {
  workflowRunBackendId: string
  workflowJobRunBackendId: string
  stepId: string
  stepNumber: number
  changeOrder: number
  status: number
}

interface ResultsData {
  lines: ResultsLogLine[]
  count: number
  startLine: number
  stepId: string
}

enum AliveMessageKind {
  StepLog = 0,
  StepsUpdate = 1,
}

@controller
class CheckStepsElement extends HTMLElement {
  @attr useActionsUx = false
  @attr loggedIn = false
  @attr jobStepsUrl = ''
  @attr jobStatus = ''
  @attr streamingUrl = ''
  @attr canStream = false
  @attr stepsFromResults = false
  #latestChangeId = 0
  #requestInProgressOrWaiting = false
  #retries = 0
  #abortController = new AbortController()
  #streamingLogSocket = SignalRSocket.getInstance('WatchRunAsync')
  #logIds = new Set<string>()
  #backscrollFetched = false
  #aliveBackscrollBuffer: Record<string, LogLine[]> = {}

  connectedCallback() {
    if (this.loggedIn) {
      this.addEventListener('socket:message', this.handleAliveEvent.bind(this))
      this.createOrCloseStreamingLogSocket()
    }
  }

  disconnectedCallback() {
    this.#abortController.abort()
    // cleanup alive listener
    this.removeEventListener('socket:message', this.handleAliveEvent.bind(this))
    // cleanup children
    this.textContent = ''
  }

  static observedAttributes = ['data-can-stream', 'data-job-status']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return

    switch (name) {
      case 'data-can-stream':
      case 'data-job-status':
        this.createOrCloseStreamingLogSocket()
        if (this.isJobCompleted()) {
          // there is a race condition on last step update via signalr socket vs. job complete over alive socket
          this.requestJobSteps()
        }
        break
      default:
      // Do nothing
    }
  }

  handleAliveEvent(event: Event) {
    const aliveData = (event as CustomEvent)?.detail
    if (!aliveData) return
    const channel = aliveData.name?.split(':')?.[0]
    switch (channel) {
      case 'check_runs': {
        const data = aliveData.data as AliveSocketData
        this.jobStatus = data.status
        this.canStream = data.streaming_logs
        break
      }
      case 'actions_results': {
        const messageKind = aliveData.data.kind
        if (messageKind === AliveMessageKind.StepLog) {
          const resultsData = aliveData.data.data as ResultsData
          this.handleAliveStepLogEvent(resultsData)
        } else if (messageKind === AliveMessageKind.StepsUpdate) {
          const stepsUpdate = aliveData.data.data as StepUpdate[]
          this.handleAliveStepUpdateEvent(stepsUpdate)
        }
        break
      }
      default:
      // Do nothing if no other matches
    }
  }

  handleAliveStepUpdateEvent(stepsUpdate: StepUpdate[]) {
    if (this.stepsFromResults) {
      const changeId = Math.min(...stepsUpdate.map(s => s.changeOrder))
      this.#latestChangeId = changeId
      this.requestJobSteps()
    }
  }

  handleAliveStepLogEvent(resultsData: ResultsData) {
    let updateJobSteps = false
    const step = stepsMap[resultsData.stepId]
    // dedup potential lines retrieved by backscrolls
    const newLogs = resultsData.lines.filter(l => !this.#logIds.has(l.lineID))
    const rawLines = newLogs.map(l => l.line)
    if (!step || !this.#backscrollFetched) {
      for (const l of newLogs) {
        this.#logIds.add(l.lineID)
      }

      this.#aliveBackscrollBuffer[resultsData.stepId] ||= []
      this.#aliveBackscrollBuffer[resultsData.stepId]?.push(...newLogs.map(({line, lineID: id}) => ({id, line})))
      updateJobSteps = true
    } else {
      const oldStepState = step.state
      updateStreamingLogs(step, rawLines, resultsData.startLine)
      updateJobSteps = oldStepState !== step.state
    }

    if (updateJobSteps) {
      this.requestJobSteps()
    }
  }

  createOrCloseStreamingLogSocket() {
    if (this.stepsFromResults) return
    if (!this.streamingUrl || !this.canStream) return

    const {signal} = this.#abortController

    if (this.jobStatus === 'completed') {
      // close socket (if exists) if the job is completed
      this.#abortController.abort()
      this.#streamingLogSocket.closeExistingSocket()
      return
    }

    this.requestJobSteps()
    this.#streamingLogSocket.startOrContinueStreaming(this.streamingUrl)

    this.#streamingLogSocket.removeEventListener('onStreamFailure', this.handleStreamFailure)
    this.#streamingLogSocket.addEventListener('onStreamFailure', this.handleStreamFailure, {signal})

    this.#streamingLogSocket.removeEventListener('socketDidReceiveMessage', this.handleSocketDidReceiveMessage)
    this.#streamingLogSocket.addEventListener('socketDidReceiveMessage', this.handleSocketDidReceiveMessage, {signal})
  }

  private handleStreamFailure = () => {
    sendStats({incrementKey: 'ACTIONS_STREAMING_LOG_VIEWS_FAIL'})
  }

  private handleSocketDidReceiveMessage = (event: Event) => {
    if (!(event instanceof CustomEvent)) return
    this.processSocketMessage(event.detail)
  }

  processSocketMessage(data: SocketData<AgentData[]>) {
    let updateJobSteps = false
    const logUpdates = Array.isArray(data.arguments) ? data.arguments : []

    for (const logUpdate of logUpdates) {
      const {lines, stepRecordId, startLine} = logUpdate
      const step = stepsMap[stepRecordId]
      if (!step) {
        bufferLogs(stepRecordId, lines)
        updateJobSteps = true
        continue
      }

      const oldStepState = step.state
      updateStreamingLogs(step, lines, startLine)
      updateJobSteps = oldStepState !== step.state
    }

    if (updateJobSteps) this.requestJobSteps()
  }

  async requestJobSteps() {
    if (this.#requestInProgressOrWaiting) return
    if (!this.jobStepsUrl) return
    if (this.hasSteps() && this.areStepsCompleted()) return

    const requestedChangeId = this.#latestChangeId

    this.#requestInProgressOrWaiting = true
    const data = await fetchJson<StepData[]>(`${this.jobStepsUrl}?change_id=${this.#latestChangeId}`)
    this.#requestInProgressOrWaiting = false
    if (!data) {
      // req err, wait and retry
      await this.waitAndIncrementRetries()
      this.requestJobSteps()
      return
    }

    // for refetching on missing data
    let dataMismatch = false

    // no data back for changeid
    if (!data.length) {
      dataMismatch = true
    }

    const ids = new Set(...bufferedStepIds())
    for (const step of data) {
      const existing = stepsMap[step.id]
      if (!existing) {
        this.addStep(step)
      } else {
        const checkStep = existing.container.closest<CheckStepElement>('check-step')!
        this.updateStepAttributes(checkStep, step)
        checkStep.update()
        await this.fetchBackscrolls(checkStep, step)
      }

      // corner case: a step is streaming without a started_at
      if (existing?.state === StepState.STREAMING && !step.started_at) {
        dataMismatch = true
      }

      // corner case: a concluded step without log url
      if (step.completed_at && !step.completed_log_url) {
        dataMismatch = true
      }

      this.#latestChangeId = Math.max(this.#latestChangeId, step.change_id)
      ids.delete(step.id)
    }

    // If there are buffered steps means we received additional logs from
    // unknown steps during the request.
    if (ids.size > 0 || dataMismatch) {
      if (this.#latestChangeId === requestedChangeId) {
        const canRetry = await this.waitAndIncrementRetries()
        if (!canRetry) return
      }
      this.requestJobSteps()
    } else {
      // if all ok, reset retries
      this.#retries = 0
    }
  }

  addStep(data: StepData) {
    const step = document.createElement('check-step')
    step.setAttribute('data-external-id', data.id)
    this.updateStepAttributes(step, data)
    this.appendChild(step)
  }

  updateStepAttributes(step: Element, data: StepData) {
    step.setAttribute('data-name', data.name)
    step.setAttribute('data-number', String(data.number))
    step.setAttribute('data-started-at', data.started_at || '')
    step.setAttribute('data-completed-at', data.completed_at || '')
    if (data.conclusion) {
      step.setAttribute('data-conclusion', data.conclusion)
      step.removeAttribute('data-in-progress')
    } else if (data.started_at) {
      step.setAttribute('data-in-progress', '')
    }
    if (data.log_url) {
      step.setAttribute('data-log-url', data.log_url)
    }
    if (this.useActionsUx) {
      step.setAttribute('data-use-actions-ux', '')
    }
    if (this.loggedIn) {
      step.setAttribute('data-logged-in', '')
    }
    if (this.isJobCompleted()) {
      step.setAttribute('data-job-completed', '')
    }
  }

  async fetchBackscrolls(checkStep: Element, step: StepData) {
    if (this.#backscrollFetched) {
      // we'll only ever want this once on page load
      return
    }

    // fetch backscroll if step is in progress if we haven't before
    if (step.started_at && !step.conclusion) {
      try {
        const jobStepBackscrollURL = checkStep.getAttribute('data-job-step-backscroll-url')
        if (!this.#backscrollFetched && jobStepBackscrollURL) {
          const backscrolls = await fetchJson<StepBackscrollLogs>(jobStepBackscrollURL)
          this.#aliveBackscrollBuffer[step.id] ||= []
          for (const line of backscrolls?.lines || []) {
            if (this.#logIds.has(line.id)) {
              continue
            }

            this.#aliveBackscrollBuffer[step.id]?.push(line)
          }
        }
      } finally {
        this.#backscrollFetched = true
        this.renderAliveBufferAndBackscrolls(step.id, this.#aliveBackscrollBuffer[step.id] || [])
      }
    }
  }

  renderAliveBufferAndBackscrolls(stepId: string, lines: LogLine[]) {
    if (!lines || lines.length === 0) return

    const step = stepsMap[stepId]
    if (!step) return

    const sortedLines = lines.sort((a, b) => a.id.localeCompare(b.id))
    const firstId = lines[0]?.id?.split('-')
    const start = parseInt(firstId?.[1] || '0')

    updateStreamingLogs(
      step,
      sortedLines.map(l => l.line),
      start,
    )
  }

  isJobCompleted() {
    return this.jobStatus === 'completed'
  }

  areStepsCompleted() {
    return [...this.querySelectorAll('check-step')].filter(step => step.hasAttribute('data-in-progress')).length === 0
  }

  hasSteps() {
    return [...this.querySelectorAll('check-step')].length > 0
  }

  async waitAndIncrementRetries(): Promise<boolean> {
    this.#requestInProgressOrWaiting = true
    // if the job is completed and all of the steps are finished, stop retrying to fetch
    const allCompleted = this.isJobCompleted() && this.areStepsCompleted()
    if (this.#retries === MAX_RETRIES || allCompleted) {
      this.#retries = 0
      this.#requestInProgressOrWaiting = false
      return false
    }
    this.#retries++
    await new Promise(res => setTimeout(res, RETRY_DELAY))
    this.#requestInProgressOrWaiting = false
    return true
  }
}
